import React from 'react';
import { motion } from 'framer-motion';
import { Users, Wind, Leaf, Sparkles } from 'lucide-react';
import { Card } from '../ui';

const Features = () => {
  const features = [
    {
      id: 1,
      icon: Users,
      title: 'Authentic Guidance',
      description: 'Learn from experienced teachers who have dedicated years to the study of yoga and meditation traditions.',
      color: 'text-pragyavan-gold',
      bg: 'bg-pragyavan-gold/10'
    },
    {
      id: 2,
      icon: Wind,
      title: 'Conscious Breathing',
      description: 'Master ancient pranayama techniques that connect your body, mind, and spirit through the power of breath.',
      color: 'text-sage-green',
      bg: 'bg-sage-green/10'
    },
    {
      id: 3,
      icon: Leaf,
      title: 'Natural Stillness',
      description: 'Practice in the serene atmosphere, where nature itself becomes your meditation teacher.',
      color: 'text-pragyavan-brown',
      bg: 'bg-pragyavan-brown/10'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-24 bg-soft-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-pragyavan-gold/5 via-transparent to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pragyavan-brown/5 text-pragyavan-brown text-xs font-medium mb-4">
            <Sparkles className="w-3 h-3" />
            <span>Why Choose Us</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-pragyavan-brown mb-6">
            The Pragyavan <span className="text-gradient-gold">Difference</span>
          </h2>
          <p className="text-lg text-warm-gray max-w-2xl mx-auto leading-relaxed">
            Experience the perfect blend of ancient wisdom and modern practices
            in your journey towards mindful living and inner peace.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {features.map((feature) => {
            const IconComponent = feature.icon;

            return (
              <motion.div
                key={feature.id}
                variants={itemVariants}
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="relative group"
              >
                {/* Gradient border wrapper */}
                <div className="absolute -inset-[1px] bg-gradient-to-br from-pragyavan-gold/0 via-transparent to-sage-green/0 group-hover:from-pragyavan-gold/40 group-hover:via-pragyavan-gold/10 group-hover:to-sage-green/30 rounded-xl transition-all duration-500 opacity-0 group-hover:opacity-100" />

                <Card
                  className="text-center h-full group-hover:shadow-xl transition-all duration-300 relative bg-white"
                  shadow="medium"
                  background="white"
                  border
                >
                  <Card.Body>
                    {/* Icon */}
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      className="mb-8 relative inline-block"
                    >
                      <div className={`
                        inline-flex items-center justify-center w-20 h-20
                        rounded-2xl rotate-3 group-hover:rotate-6 transition-all duration-300
                        ${feature.bg} group-hover:shadow-lg
                      `}>
                        <IconComponent className={`w-10 h-10 ${feature.color} -rotate-3 group-hover:-rotate-6 transition-transform duration-300`} />
                      </div>
                      {/* Glow effect behind icon */}
                      <div className={`absolute inset-0 ${feature.bg} rounded-2xl blur-xl opacity-0 group-hover:opacity-60 transition-opacity duration-300 -z-10`} />
                    </motion.div>

                    {/* Title */}
                    <h3 className="text-2xl font-display font-semibold text-pragyavan-brown mb-4 group-hover:text-pragyavan-gold transition-colors duration-300">
                      {feature.title}
                    </h3>

                    {/* Description */}
                    <p className="text-warm-gray leading-relaxed">
                      {feature.description}
                    </p>
                  </Card.Body>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-20"
        >
          <p className="text-xl text-pragyavan-brown font-display font-medium mb-8">
            Ready to begin your journey to mindfulness?
          </p>
          <motion.a
            href="/book"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center px-8 py-4 bg-pragyavan-gold text-pragyavan-brown font-bold rounded-full hover:bg-accent-gold transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-pragyavan-gold/20"
          >
            Start Your Journey
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;